import { useState, useRef } from "react";
import { useCandidatesFilter } from "@/contexts/CandidatesFilterContext";
import { CandidatesGrid } from "@/components/candidates/CandidatesGrid";
import { CandidatesList } from "@/components/candidates/CandidatesList";
import { CandidatesViewOptions, SortField } from "@/components/candidates/CandidatesViewOptions";
import { CandidatesPagination } from "@/components/candidates/CandidatesPagination";
import { useCandidatesPaginated } from "@/hooks/candidates/useCandidatesPaginated";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { Candidate } from "@/types/candidate";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { useSettings } from "@/providers/SettingsProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

// Assigned to me filter component for the sort section
const AssignedToMeFilter = () => {
  const { t } = useTranslation();
  const { assignedToMe, setAssignedToMe } = useCandidatesFilter();

  return (
    <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-input rounded-sm hover:bg-accent hover:text-accent-foreground">
      <Checkbox
        id="assigned-to-me"
        checked={assignedToMe}
        onCheckedChange={(checked) => setAssignedToMe(checked as boolean)}
        className="h-4 w-4"
      />
      <span className="text-sm">
        {t("candidates.assignedToMe", "Assigned to me")}
      </span>
    </label>
  );
};

interface CandidatesContentProps {
  candidates?: Candidate[];
  isLoading?: boolean;
  onCandidatesChange?: () => void;
}

export const CandidatesContent = ({
  onCandidatesChange
}: CandidatesContentProps) => {
  const { user } = useAuth();
  const { getGridSizeForEntity, setGridSizeForEntity } = useSettings();
  const cardMinWidth = getGridSizeForEntity("candidates", 280);
  const {
    searchTerm,
    skillsFilter,
    skillsOperator,
    statusFilter,
    functionFilter,
    assignedToMe,
    showFavoritesOnly,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode
  } = useCandidatesFilter();

  const { favorites } = useUserFavorites('candidate');

  // Use new paginated hook with database-level filtering
  const {
    candidates: paginatedCandidates,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    refetch
  } = useCandidatesPaginated(
    {
      searchTerm,
      skillsFilter,
      skillsOperator,
      statusFilter,
      functionFilter,
      assignedToMe,
      currentUserId: user?.id || null,
      showFavoritesOnly,
      favorites
    },
    sortField,
    sortOrder
  );

  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);

  // Reset to first page when sort field or order changes
  const handleSortFieldChange = (field: SortField) => {
    setSortField(field);
    setCurrentPage(1);
  };

  const handleDeleteCandidates = async (candidateIds: string[]) => {
    try {
      const { error } = await supabase
        .from('candidates')
        .delete()
        .in('id', candidateIds);

      if (error) throw error;

      toast({
        title: t("common.success"),
        description: t("common.deleteSuccess", { count: candidateIds.length }),
      });

      refetch();
      onCandidatesChange?.();
    } catch (error) {
      console.error('Error deleting candidates:', error);
      toast({
        title: t("common.error"),
        description: t("common.deleteError"),
        variant: "destructive",
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    if (containerRef.current) {
      const scrollableParent = containerRef.current.closest('.overflow-y-auto') as HTMLElement;
      if (scrollableParent) {
        // Force immediate scroll without animation
        scrollableParent.style.scrollBehavior = 'auto';
        scrollableParent.scrollTop = 0;
        scrollableParent.style.scrollBehavior = '';
      }
    }
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      <CandidatesViewOptions
        sortField={sortField}
        setSortField={handleSortFieldChange}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        viewMode={viewMode}
        setViewMode={setViewMode}
        cardMinWidth={cardMinWidth}
        setCardMinWidth={(size) => setGridSizeForEntity("candidates", size)}
        extra={
          <AssignedToMeFilter />
        }
      />
      
      <div className="flex-1 overflow-y-auto min-h-0">
        {viewMode === "card" ? (
          <CandidatesGrid
            candidates={paginatedCandidates}
            filteredCandidates={paginatedCandidates}
            isLoading={isLoading}
            onDeleteCandidates={handleDeleteCandidates}
            onCandidatesChange={onCandidatesChange}
            favorites={favorites}
            cardMinWidth={cardMinWidth}
          />
        ) : (
          <CandidatesList 
            candidates={paginatedCandidates}
            filteredCandidates={paginatedCandidates}
            isLoading={isLoading}
            onDeleteCandidates={handleDeleteCandidates}
            onCandidatesChange={onCandidatesChange}
            favorites={favorites}
          />
        )}
      </div>
      
      {totalPages > 1 && (
        <CandidatesPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
